/**
 * BilanFormModal Component
 *
 * Modal for creating or editing an accounting statement.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountingStatement,
  CreateAccountingStatementRequest,
  UpdateAccountingStatementRequest,
  BankAccount,
  OperationStatusInclusion,
  DEFAULT_OPERATION_STATUS_INCLUSION,
} from '@budgira/shared';
import { cn } from '../../utils/cn';

interface BilanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateAccountingStatementRequest | UpdateAccountingStatementRequest
  ) => Promise<void>;
  statement?: AccountingStatement | null;
  accounts: BankAccount[];
  isSubmitting: boolean;
}

export const BilanFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  statement,
  accounts,
  isSubmitting,
}: BilanFormModalProps) => {
  const { t } = useTranslation();
  const isEdit = !!statement;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [includeStatuses, setIncludeStatuses] = useState<OperationStatusInclusion>(
    DEFAULT_OPERATION_STATUS_INCLUSION
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form when modal opens or statement changes
  useEffect(() => {
    if (isOpen) {
      if (statement) {
        setName(statement.name);
        setDescription(statement.description || '');
        setStartDate(statement.startDate);
        setEndDate(statement.endDate);
        setSelectedAccountIds(statement.accounts.map((a) => a.accountId));
        setIncludeStatuses(statement.includeStatuses || DEFAULT_OPERATION_STATUS_INCLUSION);
      } else {
        // Default to current year
        const now = new Date();
        const year = now.getFullYear();
        setName(`Exercice ${year}`);
        setDescription('');
        setStartDate(`${year}-01-01`);
        setEndDate(`${year}-12-31`);
        setSelectedAccountIds([]);
        setIncludeStatuses(DEFAULT_OPERATION_STATUS_INCLUSION);
      }
      setErrors({});
    }
  }, [isOpen, statement]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('bilans.errors.nameRequired');
    }

    if (!startDate) {
      newErrors.startDate = t('bilans.errors.startDateRequired');
    }

    if (!endDate) {
      newErrors.endDate = t('bilans.errors.endDateRequired');
    }

    if (startDate && endDate && startDate > endDate) {
      newErrors.endDate = t('bilans.errors.endDateBeforeStart');
    }

    if (selectedAccountIds.length === 0) {
      newErrors.accounts = t('bilans.errors.accountRequired');
    }

    if (!includeStatuses.reconciled && !includeStatuses.pointed && !includeStatuses.unpointed) {
      newErrors.statuses = t('bilans.errors.statusRequired', 'Au moins un statut doit être sélectionné');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const data = isEdit
      ? ({
          name: name.trim(),
          description: description.trim() || undefined,
          startDate,
          endDate,
          accountIds: selectedAccountIds,
          includeStatuses,
        } as UpdateAccountingStatementRequest)
      : ({
          name: name.trim(),
          description: description.trim() || undefined,
          startDate,
          endDate,
          accountIds: selectedAccountIds,
          includeStatuses,
        } as CreateAccountingStatementRequest);

    await onSubmit(data);
  };

  const toggleAccount = (accountId: string) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
    );
  };

  const selectAllAccounts = () => {
    setSelectedAccountIds(accounts.map((a) => a.id));
  };

  const deselectAllAccounts = () => {
    setSelectedAccountIds([]);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isEdit ? t('bilans.editTitle') : t('bilans.createTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('bilans.name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={cn(
                'w-full px-3 py-2 border rounded-lg text-sm',
                'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100',
                errors.name
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-slate-300 dark:border-slate-600 focus:ring-violet-500'
              )}
              placeholder={t('bilans.namePlaceholder')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('bilans.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-violet-500"
              placeholder={t('bilans.descriptionPlaceholder')}
            />
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('bilans.startDate')} *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg text-sm',
                  'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100',
                  errors.startDate
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-slate-300 dark:border-slate-600 focus:ring-violet-500'
                )}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-500">{errors.startDate}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('bilans.endDate')} *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg text-sm',
                  'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100',
                  errors.endDate
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-slate-300 dark:border-slate-600 focus:ring-violet-500'
                )}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-500">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Accounts selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('bilans.selectAccounts')} *
              </label>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={selectAllAccounts}
                  className="text-violet-600 hover:text-violet-700 dark:text-violet-400"
                >
                  {t('common.selectAll', 'Tout sélectionner')}
                </button>
                <span className="text-slate-300">|</span>
                <button
                  type="button"
                  onClick={deselectAllAccounts}
                  className="text-violet-600 hover:text-violet-700 dark:text-violet-400"
                >
                  {t('common.deselectAll', 'Tout désélectionner')}
                </button>
              </div>
            </div>
            <div
              className={cn(
                'border rounded-lg p-3 max-h-40 overflow-y-auto',
                errors.accounts
                  ? 'border-red-500'
                  : 'border-slate-300 dark:border-slate-600'
              )}
            >
              {accounts.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('bilans.noAccounts')}
                </p>
              ) : (
                <div className="space-y-2">
                  {accounts.map((account) => (
                    <label
                      key={account.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAccountIds.includes(account.id)}
                        onChange={() => toggleAccount(account.id)}
                        className="w-4 h-4 text-violet-600 rounded border-slate-300 focus:ring-violet-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        {account.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {errors.accounts && (
              <p className="mt-1 text-sm text-red-500">{errors.accounts}</p>
            )}
          </div>

          {/* Operation status inclusion */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {t('bilans.includeOperations', 'Opérations à inclure')}
            </label>
            <div className="space-y-2 p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700/50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeStatuses.reconciled}
                  onChange={(e) =>
                    setIncludeStatuses((prev) => ({
                      ...prev,
                      reconciled: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 text-violet-600 rounded border-slate-300 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {t('bilans.includeReconciled', 'Réconciliées')}
                  <span className="text-xs text-slate-500 ml-1">
                    ({t('bilans.reconciledDescription', 'vérifiées avec la banque')})
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeStatuses.pointed}
                  onChange={(e) =>
                    setIncludeStatuses((prev) => ({
                      ...prev,
                      pointed: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 text-violet-600 rounded border-slate-300 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {t('bilans.includePointed', 'Pointées')}
                  <span className="text-xs text-slate-500 ml-1">
                    ({t('bilans.pointedDescription', 'vérifiées manuellement')})
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeStatuses.unpointed}
                  onChange={(e) =>
                    setIncludeStatuses((prev) => ({
                      ...prev,
                      unpointed: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 text-violet-600 rounded border-slate-300 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {t('bilans.includeUnpointed', 'Non pointées')}
                  <span className="text-xs text-slate-500 ml-1">
                    ({t('bilans.unpointedDescription', 'en attente de vérification')})
                  </span>
                </span>
              </label>
            </div>
            {!includeStatuses.reconciled && !includeStatuses.pointed && !includeStatuses.unpointed && (
              <p className="mt-1 text-sm text-amber-500">
                {t('bilans.noStatusSelected', 'Au moins un statut doit être sélectionné')}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {isSubmitting
                ? t('common.saving')
                : isEdit
                  ? t('common.save')
                  : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default BilanFormModal;
